import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet } from './api'
import { qk } from './queryClient'
import { subscribeNotifBadge } from './rt'
import { escritorio, esRutaInterna } from './desktop'
import type { NotifItem } from './types'

// Notificaciones del SISTEMA en la app de escritorio.
//
// Electron no tiene Web Push (lib/push.ts ya reporta pushSupported()=false), así
// que no hay servidor empujando nada al cascarón. Lo que sí hay es el MISMO flujo
// en vivo que alimenta la campanita: el listener al doc notifications/{email}
// (lib/rt.ts) avisa que el contador cambió, y la lista real se pide a la API.
// Sobre eso se monta esto: cuando aparece una notificación NUEVA se la pasamos a
// window.desktop.notify con el MISMO título, cuerpo y link que muestra la
// campanita — es la misma notificación en otra superficie, no una copia con otro
// texto. Si mañana el API cambia el copy, cambia en las dos a la vez.

// ── Marca de "hasta aquí ya vi" ───────────────────────────────────────────────
// El problema a evitar: al montar, la lista trae las notificaciones históricas y
// dispararlas todas soltaría veinte globos del sistema de golpe. La marca guarda
// el instante de la más reciente ya conocida (y los ids que caen EXACTAMENTE en
// ese instante, porque dos notificaciones pueden compartir milisegundo y con solo
// la fecha se perdería una).
//
// Va en sessionStorage y no en localStorage a propósito: la marca es de ESTA
// ventana. Si se guardara en disco, abrir la app tras el fin de semana notificaría
// como "nuevo" todo lo acumulado; y al revés, dos ventanas se pisarían la marca.

export interface MarcaNotifs {
  /** ISO de la notificación más reciente ya vista. Cadena vacía = MARCA_VACIA. */
  at: string
  /** Ids que caen exactamente en ese instante. */
  ids: string[]
}

/**
 * "Empezamos a mirar y no había NADA". Es una marca de verdad —ya hubo montaje—
 * pero sin línea base de tiempo.
 *
 * POR QUÉ EXISTE Y NO SE USA Date.now(): la marca se compara contra el campo `at`
 * que escribe el SERVIDOR. Plantarla en el reloj del cliente mezcla dos relojes, y
 * en las PCs sin NTP eso se nota: con el reloj adelantado, todo lo que se cree
 * mientras dura el desfase nace "más viejo" que la marca y no se anuncia nunca;
 * con el reloj atrasado se re-anuncia lo ya visto. Y no hace falta: si la lista
 * estaba vacía, cualquier cosa que aparezca después ES nueva, sin necesidad de
 * comparar fechas con nadie.
 */
export const MARCA_VACIA: MarcaNotifs = { at: '', ids: [] }

/** ¿La marca es la de "no había nada"? */
export function esMarcaVacia(marca: MarcaNotifs | null): boolean {
  return marca !== null && marca.at === ''
}

const claveMarca = (email: string) => `camp_notifs_esc:${email.toLowerCase()}`

// Techo de ids: solo importan los del mismo milisegundo, pero un dato corrupto no
// puede hacer crecer la clave sin fin.
const MAX_IDS = 20

export function leerMarca(email: string): MarcaNotifs | null {
  try {
    const crudo = sessionStorage.getItem(claveMarca(email))
    if (!crudo) return null
    const m = JSON.parse(crudo) as Partial<MarcaNotifs>
    if (typeof m?.at !== 'string' || !Array.isArray(m.ids)) return null
    return { at: m.at, ids: m.ids.filter((i): i is string => typeof i === 'string') }
  } catch {
    // sessionStorage puede estar bloqueado o traer basura: sin marca se trata
    // como primer arranque (baseline sin notificar), que es el lado seguro.
    return null
  }
}

export function guardarMarca(email: string, marca: MarcaNotifs): void {
  try {
    sessionStorage.setItem(claveMarca(email), JSON.stringify(marca))
  } catch {
    // Si no se puede guardar, lo peor que pasa es no volver a notificar hasta
    // que se pueda. Nunca duplicar es preferible a llenar la pantalla.
  }
}

const instante = (iso: string): number => {
  const t = Date.parse(iso)
  return Number.isFinite(t) ? t : NaN
}

/**
 * Las que hay que anunciar: más nuevas que la marca y SIN LEER.
 *
 * Sin marca (null) devuelve vacío a propósito — ese es el montaje, y lo histórico
 * no se anuncia. Con MARCA_VACIA es al revés: ya hubo montaje y no había nada, así
 * que lo que aparezca ahora sí es novedad. Las ya leídas nunca: si alguien las
 * abrió en el navegador del teléfono, que el Mac no ladre por ellas.
 */
export function nuevasDesde(items: NotifItem[], marca: MarcaNotifs | null): NotifItem[] {
  if (!marca) return []
  // Con MARCA_VACIA no hay corte que comparar: la lista estaba vacía, así que todo
  // lo que aparezca es nuevo. (Las de fecha ilegible siguen fuera: si se anunciaran,
  // avanzarMarca no podría moverse y se repetirían en cada refresco.)
  const vacia = esMarcaVacia(marca)
  const corte = vacia ? NaN : instante(marca.at)
  if (!vacia && Number.isNaN(corte)) return []
  return items
    .filter((n) => {
      if (n.read) return false
      const t = instante(n.at)
      if (Number.isNaN(t)) return false // fecha ilegible: no se inventa novedad
      if (vacia) return true
      if (t > corte) return true
      return t === corte && !marca.ids.includes(n.id)
    })
    .sort((a, b) => instante(a.at) - instante(b.at)) // se anuncian en orden
}

/** La marca movida hasta la notificación más reciente de la lista. */
export function avanzarMarca(items: NotifItem[], marca: MarcaNotifs | null): MarcaNotifs {
  let mejor = marca && !esMarcaVacia(marca) ? instante(marca.at) : NaN
  for (const n of items) {
    const t = instante(n.at)
    if (Number.isNaN(t)) continue
    if (Number.isNaN(mejor) || t > mejor) mejor = t
  }
  // Nada con fecha usable (lista vacía y sin marca previa): se queda en MARCA_VACIA.
  // Ni el año cero —que arrastraría todo el histórico en cuanto llegara una— ni el
  // reloj del cliente, que no es el que fecha las notificaciones.
  if (Number.isNaN(mejor)) return MARCA_VACIA
  const at = new Date(mejor).toISOString()
  const ids = items
    .filter((n) => instante(n.at) === mejor)
    .map((n) => n.id)
    .slice(0, MAX_IDS)
  // Se conservan los ids viejos del mismo instante para no re-anunciar uno que ya
  // salió y que la API todavía devuelve.
  const previos = marca && instante(marca.at) === mejor ? marca.ids : []
  return { at, ids: [...new Set([...previos, ...ids])].slice(0, MAX_IDS) }
}

// ── El enganche ───────────────────────────────────────────────────────────────

// Identidad estable: react-query vuelve a correr `select` si la función cambia,
// y una flecha inline crearía un array nuevo en cada render.
const soloItems = (d: { items: NotifItem[] }) => d.items

/**
 * Engancha las notificaciones del sistema y la navegación desde ellas. En
 * navegador no hace absolutamente nada (ni listeners ni queries).
 */
export function useNotifsEscritorio(email: string | null | undefined): void {
  const d = escritorio()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const activo = Boolean(d && email)

  // Insignia del dock + disparo de la recarga de la lista. El listener es el que
  // YA usa la campanita (un doc, no una colección): no se añade tráfico realtime,
  // se aprovecha el que existe.
  useEffect(() => {
    if (!d || !email) return
    const quitar = subscribeNotifBadge(email, (unread) => {
      void d.setBadge(unread).catch(() => {})
      // El contador cambió: la lista que tenemos ya no es la de ahora.
      void qc.invalidateQueries({ queryKey: qk.notifs })
    })
    return () => {
      quitar()
      // Al cerrar sesión la insignia se queda pegada en el dock si no se limpia.
      void d.setBadge(0).catch(() => {})
    }
  }, [d, email, qc])

  // La MISMA query de la campanita (misma llave): el panel y esto comparten datos
  // y una sola petición.
  const { data: items } = useQuery({
    queryKey: qk.notifs,
    queryFn: () => apiGet<{ items: NotifItem[] }>('/api/notifications'),
    select: soloItems,
    enabled: activo,
  })

  useEffect(() => {
    if (!d || !email || !items) return
    const marca = leerMarca(email)
    // Guardar ANTES de notificar: notify() es asíncrono y un re-render en medio
    // volvería a leer la marca vieja y anunciaría dos veces lo mismo.
    const nuevas = nuevasDesde(items, marca)
    guardarMarca(email, avanzarMarca(items, marca))
    for (const n of nuevas) {
      void d
        .notify({
          id: n.id,
          title: n.title,
          body: n.body,
          // El link viaja tal cual lo generó la API; si no es una ruta de camp,
          // la notificación sale igual pero sin destino (el clic solo enfoca).
          link: esRutaInterna(n.link) ? n.link : undefined,
        })
        .catch(() => {})
    }
  }, [d, email, items])

  // Clic en la notificación del sistema (o deep link): el main pide navegar.
  useEffect(() => {
    if (!d) return
    return d.onNavigate((link) => {
      if (!esRutaInterna(link)) {
        console.warn('escritorio: link de navegación descartado', link)
        return
      }
      navigate(link)
    })
  }, [d, navigate])
}
